// client/src/components/AiChatWindow.js

import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  IconButton, 
  Fab, 
  Slide, 
  Fade,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import axiosInstance from '../axiosInstance';

// Styled components
const ChatWindowContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  width: '350px',
  height: '500px',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1300,
  overflow: 'hidden',
  backdropFilter: 'blur(10px)',
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[10],
  borderRadius: '12px',
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
}));

const ChatBody = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
}));

const ChatFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
}));

const MessageBubble = styled(Box)(({ theme, isUser }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: '18px',
  maxWidth: '80%',
  wordBreak: 'break-word',
  marginBottom: theme.spacing(1),
  backgroundColor: isUser ? theme.palette.primary.main : theme.palette.grey[200],
  color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
  alignSelf: isUser ? 'flex-end' : 'flex-start',
}));

const ChatFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  zIndex: 1200,
}));

/**
 * AI Chat Window component that provides a floating chat interface
 * for interacting with the AI assistant
 */
const AiChatWindow = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const chatBodyRef = useRef(null);
  
  // Get user ID from JWT token for more reliable identification
  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return 'anonymous';
      
      // Parse the JWT token to get the user ID
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const payload = JSON.parse(jsonPayload);
      return payload.id || 'anonymous';
    } catch (error) {
      console.error('Error extracting user ID from token:', error);
      return 'anonymous';
    }
  };
  
  const userId = getUserIdFromToken();

  // Load conversation history from localStorage on component mount
  useEffect(() => {
    const savedState = localStorage.getItem(`aiChatState_${userId}`);
    if (savedState) {
      try {
        const { isOpen: savedIsOpen, conversation: savedConversation } = JSON.parse(savedState);
        setIsOpen(savedIsOpen);
        setConversation(savedConversation);
      } catch (error) {
        console.error('Error parsing saved chat state:', error);
      }
    }
  }, [userId]); // Add userId as dependency to reload when user changes

  // Save conversation history and open state to localStorage
  useEffect(() => {
    localStorage.setItem(`aiChatState_${userId}`, JSON.stringify({
      isOpen,
      conversation
    }));
  }, [isOpen, conversation, userId]); // Add userId as dependency

  // Scroll to bottom when new messages are added
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [conversation]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const sendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = message.trim();
    setMessage('');
    
    // Add user message to conversation
    setConversation(prev => [...prev, { role: 'user', content: userMessage }]);
    
    // Set loading state
    setLoading(true);
    
    try {
      // Send message to AI service
      const response = await axiosInstance.post('/ai/chat', {
        user_id: userId,
        prompt: userMessage,
        enable_plugins: true
      });
      
      // Add AI response to conversation
      setConversation(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error('Error sending message to AI:', error);
      // Add error message to conversation
      setConversation(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again later.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearConversation = () => {
    setConversation([]);
  };

  return (
    <>
      {/* Chat toggle button */}
      <Fade in={!isOpen}>
        <ChatFab 
          color="primary" 
          aria-label="chat"
          onClick={toggleChat}
        >
          <ChatIcon />
        </ChatFab>
      </Fade>

      {/* Chat window */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <ChatWindowContainer>
          <ChatHeader>
            <Typography variant="h6">AI Assistant</Typography>
            <Box>
              <IconButton 
                size="small" 
                onClick={clearConversation}
                color="inherit"
              >
                <Typography variant="caption">Clear</Typography>
              </IconButton>
              <IconButton 
                size="small" 
                onClick={toggleChat}
                color="inherit"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </ChatHeader>
          
          <ChatBody ref={chatBodyRef}>
            {conversation.length === 0 ? (
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  opacity: 0.7
                }}
              >
                <ChatIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body2" color="textSecondary" align="center">
                  Welcome to the InterCollect AI Assistant!
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  Ask me anything about your collections or how to use the app.
                </Typography>
              </Box>
            ) : (
              conversation.map((msg, index) => (
                <MessageBubble key={index} isUser={msg.role === 'user'}>
                  <Typography variant="body2">
                    {msg.content}
                  </Typography>
                </MessageBubble>
              ))
            )}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </ChatBody>
          
          <ChatFooter>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Ask a question..."
              value={message}
              onChange={handleMessageChange}
              onKeyPress={handleKeyPress}
              disabled={loading}
              multiline
              maxRows={3}
              sx={{ mr: 1 }}
            />
            <IconButton 
              color="primary" 
              onClick={sendMessage}
              disabled={!message.trim() || loading}
            >
              <SendIcon />
            </IconButton>
          </ChatFooter>
        </ChatWindowContainer>
      </Slide>
    </>
  );
};

export default AiChatWindow;
